/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { PrototypeTask as PrototypeTaskComponent } from '@userzoomplatform/pe-study-tasks';
import { PrototypeTaskData, PrototypeTaskAnswer } from '@userzoomplatform/pe-study-tasks/dist/lib/types/task';
import { Texts } from '@userzoomplatform/pe-study-tasks/dist/lib/texts/types';
import { gqlQuestionTypes } from '../../../utils/constants';
import { useToken } from '../../../hooks';
import { TASKS_FIELDS } from '../fragments';
import config from '../../../utils/config';
import { Namespaces } from '../../../utils/i18n';

export interface Props {
	data: PrototypeTaskData;
	systemTexts: Texts['systemTexts'];
	setTask: (task: PrototypeTaskData) => void;
}

const defaultProps = {};

const answerPrototypeTaskMutation = gql`
	${TASKS_FIELDS}
	mutation AnswerPrototypeTask($answer: AnswerPrototypeTask!) {
		answerPrototypeTask(answer: $answer) {
			...TasksFields
		}
	}
`;

const PrototypeTask = ({ data, systemTexts, setTask }: Props): ReactElement => {
	const token = useToken();
	const { assetsBasePath } = config();
	const { t } = useTranslation([Namespaces.Desktop]);

	const [answerPrototypeTask, response] = useMutation(answerPrototypeTaskMutation, {
		context: { headers: { authorization: `Bearer ${token}` } },
	});

	const onAnswer = useCallback(
		async (answer: PrototypeTaskAnswer) => {
			const { type } = data.question;
			answerPrototypeTask({
				variables: {
					answer: {
						questionnaireId: data.id,
						answer: {
							type: gqlQuestionTypes[type],
							[gqlQuestionTypes[type]]: answer,
						},
					},
				},
			});
		},
		[answerPrototypeTask, data]
	);

	useEffect(() => {
		if (response.error) {
			// TODO: handle error
			// eslint-disable-next-line no-console
			console.error(response.error);
		}

		if (response?.data?.answerPrototypeTask) {
			setTask(response?.data?.answerPrototypeTask);
		}
	}, [response, setTask]);

	const texts = {
		systemTexts,
		copies: {
			optionalQuestionLabel: t(`${Namespaces.Desktop}:common.optional`),
		},
	};

	return <PrototypeTaskComponent data={{ ...data, onAnswer }} texts={texts} config={{ assetsBasePath }} />;
};

PrototypeTask.defaultProps = defaultProps;

export default PrototypeTask;
